/**
 * 套餐购买页面：展示会员套餐、算力包与商品采集助手席位，并提供购买与下载入口。
 * 简洁高转化设计，保持业务接口与调用方式不变。
 */

import type { ReactElement } from 'react'
import { startTransition, useCallback, useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import {
  AlertCircle,
  ArrowRight,
  Battery,
  Bot,
  Check,
  Clock3,
  Crown,
  Download,
  Globe,
  LockKeyhole,
  Layers3,
  Monitor,
  Scissors,
  ShieldCheck,
  Sparkles,
  Zap,
} from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { useAllPackages } from '@/hooks/usePackages'
import type { GroupedPackages } from '@/hooks/usePackages'
import { PaymentDialog } from '@/components/PaymentDialog'
import { getLatestExtensionRelease } from '@/services/extensionReleaseService'
import { useAuthStore } from '@/stores'
import type { ExtensionLatestRelease } from '@/types/extensionRelease'
import type { Package, PackageType } from '@/types/package'

/** 主 Tab 类型 */
type MainTabType = 'subscription' | 'recharge' | 'clientSeat'

/** 购买回调签名（接口边界，保持不变） */
interface PurchaseOptions {
  customCredits?: number
  quantity?: number
  purchaseMonths?: number
}

type PurchaseHandler = (pkg: Package, options?: PurchaseOptions) => void

const CUSTOM_RECHARGE_MIN = 60000
const CUSTOM_RECHARGE_MAX = 1000000
const CUSTOM_RECHARGE_STEP = 1000
const CUSTOM_RECHARGE_PRICE_UNIT_CREDITS = 100
const CLIENT_SEAT_MIN = 1
const CLIENT_SEAT_MAX = 20
const CLIENT_SEAT_MONTHS_MIN = 1
const CLIENT_SEAT_MONTHS_MAX = 12
const CLIENT_SEAT_DAYS_PER_MONTH = 30

interface ClientAssistantFeatureItem {
  icon: typeof Monitor
  title: string
  description: string
}

interface ClientAssistantStepItem {
  step: string
  title: string
}

const CLIENT_ASSISTANT_FEATURES = [
  {
    icon: Globe,
    title: '批量采集商品信息',
    description: '支持批量整理标题、价格、规格与主图，减少重复复制。',
  },
  {
    icon: Scissors,
    title: '自动裁剪图片',
    description: '自动裁剪并规整商品图尺寸，让素材更便于后续上架。',
  },
  {
    icon: Layers3,
    title: '统一整理素材',
    description: '图文结果统一归档，方便团队继续校对、编辑与分发。',
  },
] as const satisfies readonly ClientAssistantFeatureItem[]

const CLIENT_ASSISTANT_STEPS = [
  { step: '01', title: '导入链接' },
  { step: '02', title: '批量采集' },
  { step: '03', title: '裁剪整理' },
] as const satisfies readonly ClientAssistantStepItem[]
const defaultReleaseChannel = 'stable'
const CLIENT_ASSISTANT_DOWNLOAD_ID = 'client-assistant-download' as const
const CLIENT_ASSISTANT_DOWNLOAD_HASH = '#client-assistant-download' as const

/**
 * 判断当前 hash 是否定位到商品采集助手下载区。
 * @param hash 页面 hash
 * @returns 是否为商品采集助手下载区 hash
 */
function isClientAssistantDownloadHash(hash: string): boolean {
  return hash === CLIENT_ASSISTANT_DOWNLOAD_HASH
}

/**
 * 从客户端发布信息中提取推荐下载文件名。
 * @param release 客户端最新发布信息
 * @returns 推荐下载文件名
 */
function getReleaseDownloadFilename(release: ExtensionLatestRelease): string {
  try {
    const url = new URL(release.downloadUrl)
    const pathname = url.pathname
    const filename = pathname.split('/').pop()

    if (filename !== undefined && filename !== '') {
      return decodeURIComponent(filename)
    }
  } catch {
    // URL 解析失败时回退到默认命名
  }

  return `yuexi-seller-helper-${release.version}.zip`
}

function formatReleasePublishedAt(value: string): string | null {
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return null
  }

  return new Intl.DateTimeFormat('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(parsed)
}

/**
 * 获取会员套餐图标元素。
 * @param packageType 套餐类型
 * @param className 图标样式类名
 * @returns 图标元素
 */
function getSubscriptionIcon(packageType: PackageType, className: string): ReactElement {
  return packageType === 'yearly' || packageType === 'quarterly'
    ? <Zap className={className} />
    : <Sparkles className={className} />
}

/**
 * 获取会员套餐周期文案。
 * @param packageType 套餐类型
 * @returns 周期文案
 */
function getSubscriptionPeriodLabel(packageType: PackageType): string {
  switch (packageType) {
    case 'weekly':
      return '7天'
    case 'monthly':
      return '月'
    case 'quarterly':
      return '季'
    case 'yearly':
      return '年'
    default:
      return '周期'
  }
}

/**
 * 获取套餐周期天数，用于计算日均成本。
 * @param packageType 套餐类型
 * @returns 对应天数
 */
function getSubscriptionCycleDays(packageType: PackageType): number {
  switch (packageType) {
    case 'weekly':
      return 7
    case 'monthly':
      return 30
    case 'quarterly':
      return 90
    case 'yearly':
      return 365
    default:
      return 1
  }
}

/**
 * 价格格式化：整数显示整数，小数保留两位。
 * @param amount 金额（元）
 * @returns 格式化后的金额
 */
function formatMoney(amount: number): string {
  return Number.isInteger(amount) ? String(amount) : amount.toFixed(2)
}

/**
 * 判断会员卡片是否推荐展示。
 * @param pkg 套餐信息
 * @returns 是否推荐
 */
function isRecommendedSubscription(pkg: Package): boolean {
  return pkg.packageType == 'quarterly'
}

/**
 * 主 Tab 切换组件 Props
 */
interface MainTabSwitcherProps {
  activeTab: MainTabType
  hasSubscription: boolean
  hasRecharge: boolean
  hasClientSeat: boolean
  onTabChange: (tab: MainTabType) => void
}

/**
 * 主 Tab 切换组件。
 */
function MainTabSwitcher({
  activeTab,
  hasSubscription,
  hasRecharge,
  hasClientSeat,
  onTabChange,
}: MainTabSwitcherProps): ReactElement {
  return (
    <div className="inline-flex rounded-xl bg-muted/60 p-1">
      {hasSubscription ? (
        <button
          type="button"
          onClick={(): void => {
            onTabChange('subscription')
          }}
          className={cn(
            'relative rounded-lg px-5 py-2 text-sm font-medium transition-all',
            activeTab === 'subscription'
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          )}
        >
          <span className="inline-flex items-center gap-1.5">
            <Clock3 className="h-3.5 w-3.5" />
            会员套餐
          </span>
        </button>
      ) : null}
      {hasRecharge ? (
        <button
          type="button"
          onClick={(): void => {
            onTabChange('recharge')
          }}
          className={cn(
            'relative rounded-lg px-5 py-2 text-sm font-medium transition-all',
            activeTab === 'recharge'
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          )}
        >
          <span className="inline-flex items-center gap-1.5">
            <Battery className="h-3.5 w-3.5" />
            算力包
          </span>
        </button>
      ) : null}
      {hasClientSeat ? (
        <button
          type="button"
          onClick={(): void => {
            onTabChange('clientSeat')
          }}
          className={cn(
            'relative rounded-lg px-5 py-2 text-sm font-medium transition-all',
            activeTab === 'clientSeat'
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          )}
        >
          <span className="inline-flex items-center gap-1.5">
            <Monitor className="h-3.5 w-3.5" />
            商品采集助手
          </span>
        </button>
      ) : null}
    </div>
  )
}

/**
 * 会员套餐卡片 Props。
 */
interface SubscriptionCardProps {
  pkg: Package
  onPurchase: PurchaseHandler
}

/**
 * 会员套餐卡片。
 */
function SubscriptionCard({ pkg, onPurchase }: SubscriptionCardProps): ReactElement {
  const iconElement = getSubscriptionIcon(pkg.packageType, 'h-4.5 w-4.5')
  const periodLabel = getSubscriptionPeriodLabel(pkg.packageType)
  const cycleDays = getSubscriptionCycleDays(pkg.packageType)
  const dailyCost = pkg.priceYuan / cycleDays
  const recommended = isRecommendedSubscription(pkg)

  const handlePurchase = useCallback((): void => {
    onPurchase(pkg)
  }, [onPurchase, pkg])

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className={cn(
        'group relative flex h-full flex-col rounded-2xl border bg-card transition-all hover:-translate-y-1 hover:shadow-lg',
        recommended
          ? 'border-primary/50 shadow-md shadow-primary/10'
          : 'border-border/60 hover:border-primary/30'
      )}
    >
      {recommended ? (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground shadow-sm">
            <Crown className="h-3 w-3" />
            推荐
          </span>
        </div>
      ) : null}

      <div className="p-6 pb-4">
        <div className="flex items-center gap-2.5">
          <div
            className={cn(
              'flex h-9 w-9 shrink-0 items-center justify-center rounded-xl',
              recommended ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
            )}
          >
            {iconElement}
          </div>
          <h3 className="text-base font-bold text-foreground">{pkg.name}</h3>
        </div>

        <div className="mt-5">
          <div className="flex items-baseline gap-0.5">
            <span className="text-sm font-medium text-muted-foreground">¥</span>
            <span className="text-4xl font-black tracking-tight text-foreground">
              {formatMoney(pkg.priceYuan)}
            </span>
            <span className="ml-0.5 text-sm text-muted-foreground">/{periodLabel}</span>
          </div>
          {pkg.originalPriceYuan !== null && pkg.originalPriceYuan > pkg.priceYuan ? (
            <div className="mt-1.5 flex items-center gap-2">
              <span className="text-xs text-muted-foreground/70 line-through">
                ¥{formatMoney(pkg.originalPriceYuan)}
              </span>
              {pkg.discountPercentage !== null ? (
                <span className="rounded bg-red-500/10 px-1.5 py-0.5 text-[11px] font-semibold text-red-600">
                  -{pkg.discountPercentage}%
                </span>
              ) : null}
            </div>
          ) : null}
          <p className="mt-1 text-xs text-muted-foreground">约 ¥{dailyCost.toFixed(2)}/天</p>
        </div>
      </div>

      <div className="flex-1 border-t border-dashed px-6 py-4">
        <ul className="space-y-2.5">
          {pkg.features.map((feature) => (
            <li key={feature} className="flex items-start gap-2 text-sm">
              <Check
                className={cn(
                  'mt-0.5 h-4 w-4 shrink-0',
                  recommended ? 'text-primary' : 'text-muted-foreground'
                )}
              />
              <span className="text-foreground/85">{feature}</span>
            </li>
          ))}
          <li className="flex items-start gap-2 text-sm">
            <Battery
              className={cn(
                'mt-0.5 h-4 w-4 shrink-0',
                recommended ? 'text-primary' : 'text-muted-foreground'
              )}
            />
            <span className="text-foreground/85">
              赠送算力 {pkg.bonusCredits.toLocaleString()}
            </span>
          </li>
        </ul>
      </div>

      <div className="p-6 pt-2">
        <Button
          className={cn('w-full gap-1.5', recommended && 'shadow-sm')}
          size="lg"
          variant={recommended ? 'default' : 'outline'}
          onClick={handlePurchase}
        >
          立即开通
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </motion.div>
  )
}

/**
 * 算力包卡片 Props。
 */
interface RechargeCardProps {
  pkg: Package
  onPurchase: PurchaseHandler
}

/**
 * 算力包卡片。
 */
function RechargeCard({ pkg, onPurchase }: RechargeCardProps): ReactElement {
  const valuePerYuan =
    pkg.priceYuan > 0 ? Math.round((pkg.totalCredits / pkg.priceYuan) * 10) / 10 : 0
  const recommended = pkg.isRecommended

  const handlePurchase = useCallback((): void => {
    onPurchase(pkg)
  }, [onPurchase, pkg])

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className={cn(
        'group relative flex h-full flex-col rounded-2xl border bg-card transition-all hover:-translate-y-1 hover:shadow-lg',
        recommended
          ? 'border-amber-500/50 shadow-md shadow-amber-500/10'
          : 'border-border/60 hover:border-amber-500/30'
      )}
    >
      {recommended ? (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-500 px-3 py-1 text-xs font-semibold text-white shadow-sm">
            <Zap className="h-3 w-3" />
            高性价比
          </span>
        </div>
      ) : null}

      <div className="p-6 pb-4">
        <h3 className="text-base font-bold text-foreground">{pkg.name}</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          {pkg.features.length > 0 ? pkg.features[0] : '按需补充，永久有效'}
        </p>

        <div className="mt-5 flex items-baseline gap-0.5">
          <span className="text-sm font-medium text-muted-foreground">¥</span>
          <span className="text-4xl font-black tracking-tight text-foreground">
            {formatMoney(pkg.priceYuan)}
          </span>
        </div>
        {pkg.originalPriceYuan !== null && pkg.originalPriceYuan > pkg.priceYuan ? (
          <div className="mt-1.5">
            <span className="text-xs text-muted-foreground/70 line-through">
              ¥{formatMoney(pkg.originalPriceYuan)}
            </span>
          </div>
        ) : null}
      </div>

      <div className="flex-1 border-t border-dashed px-6 py-4">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">到账算力</span>
            <span className="text-lg font-bold text-foreground">
              {pkg.totalCredits.toLocaleString()}
            </span>
          </div>
          {pkg.bonusCredits > 0 ? (
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">含赠送</span>
              <span className="text-sm font-semibold text-amber-600">
                +{pkg.bonusCredits.toLocaleString()}
              </span>
            </div>
          ) : null}
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">单价</span>
            <span className="text-sm font-medium text-foreground">
              {valuePerYuan} 算力/元
            </span>
          </div>
        </div>
      </div>

      <div className="p-6 pt-2">
        <Button
          className="w-full gap-1.5"
          size="lg"
          variant={recommended ? 'default' : 'outline'}
          onClick={handlePurchase}
        >
          立即购买
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </motion.div>
  )
}

/**
 * 自定义算力购买组件 Props。
 */
interface CustomRechargePanelProps {
  pkg: Package
  onPurchase: PurchaseHandler
  className?: string
}

/**
 * 自定义算力购买面板。
 */
function CustomRechargePanel({ pkg, onPurchase, className }: CustomRechargePanelProps): ReactElement {
  const [inputValue, setInputValue] = useState<string>(String(CUSTOM_RECHARGE_MIN))
  const inputId = `custom-credits-input-${pkg.id}`

  const parsedCredits = Number(inputValue)
  const hasInput = inputValue.trim().length > 0
  const isIntegerCredits = Number.isInteger(parsedCredits)

  let validationMessage: string | null = null
  if (!hasInput) {
    validationMessage = '请输入购买算力'
  } else if (!Number.isFinite(parsedCredits) || !isIntegerCredits) {
    validationMessage = '请输入整数点数'
  } else if (parsedCredits < CUSTOM_RECHARGE_MIN) {
    validationMessage = `最小值为 ${CUSTOM_RECHARGE_MIN.toLocaleString()}`
  } else if (parsedCredits > CUSTOM_RECHARGE_MAX) {
    validationMessage = `不能超过 ${CUSTOM_RECHARGE_MAX.toLocaleString()}`
  } else if (parsedCredits % CUSTOM_RECHARGE_STEP !== 0) {
    validationMessage = `必须按 ${CUSTOM_RECHARGE_STEP.toLocaleString()} 递增`
  }

  const isValid = validationMessage === null
  const amountYuan = isValid
    ? (parsedCredits / CUSTOM_RECHARGE_PRICE_UNIT_CREDITS) * pkg.customPricePer100 / 100
    : 0
  const valuePerYuan = isValid && amountYuan > 0
    ? Math.round((parsedCredits / amountYuan) * 10) / 10
    : 0

  const handlePurchase = useCallback((): void => {
    if (!isValid) {
      return
    }
    onPurchase(pkg, { customCredits: parsedCredits })
  }, [isValid, onPurchase, parsedCredits, pkg])

  const handleQuickSelect = useCallback((credits: number): void => {
    setInputValue(String(credits))
  }, [])

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className={cn(
        'group relative flex h-full flex-col rounded-2xl border border-primary/40 bg-card shadow-md shadow-primary/5 transition-all hover:-translate-y-1 hover:shadow-lg',
        className
      )}
    >
      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
        <span className="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground shadow-sm">
          <Sparkles className="h-3 w-3" />
          灵活定制
        </span>
      </div>

      <div className="p-6 pb-4">
        <h3 className="text-base font-bold text-foreground">自定义算力</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          {CUSTOM_RECHARGE_MIN.toLocaleString()} 起充，按需灵活配置
        </p>
      </div>

      <div className="flex-1 border-t border-dashed px-6 py-4">
        <div className="space-y-3">
          <div>
            <label htmlFor={inputId} className="text-sm font-medium text-foreground">
              算力点数
            </label>
            <Input
              id={inputId}
              type="number"
              min={CUSTOM_RECHARGE_MIN}
              max={CUSTOM_RECHARGE_MAX}
              step={CUSTOM_RECHARGE_STEP}
              value={inputValue}
              onChange={(event): void => {
                setInputValue(event.target.value)
              }}
              className="mt-1.5 h-10 text-base"
              placeholder="请输入算力点数"
            />
          </div>

          <div className="flex flex-wrap gap-1.5">
            {[60000, 120000, 300000].map((credits) => (
              <button
                key={credits}
                type="button"
                onClick={(): void => {
                  handleQuickSelect(credits)
                }}
                className={cn(
                  'rounded-lg border px-2.5 py-1 text-xs font-medium transition-colors',
                  Number(inputValue) === credits
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border text-muted-foreground hover:border-primary/40 hover:text-foreground'
                )}
              >
                {credits.toLocaleString()}
              </button>
            ))}
          </div>

          {validationMessage !== null ? (
            <p className="text-xs text-destructive">{validationMessage}</p>
          ) : (
            <p className="text-xs text-muted-foreground">按 {CUSTOM_RECHARGE_STEP.toLocaleString()} 点递增</p>
          )}

          <div className="rounded-xl bg-muted/50 p-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">应付</span>
              <div className="flex items-baseline gap-0.5">
                <span className="text-sm text-muted-foreground">¥</span>
                <span className="text-2xl font-black tracking-tight text-foreground">
                  {amountYuan.toFixed(2)}
                </span>
              </div>
            </div>
            {isValid && valuePerYuan > 0 ? (
              <p className="mt-1 text-right text-xs text-muted-foreground">
                约 {valuePerYuan} 算力/元
              </p>
            ) : null}
          </div>
        </div>
      </div>

      <div className="p-6 pt-2">
        <Button className="w-full gap-1.5" size="lg" disabled={!isValid} onClick={handlePurchase}>
          立即购买
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </motion.div>
  )
}

/**
 * 客户端席位购买卡片 Props。
 */
interface ClientSeatCardProps {
  pkg: Package
  onPurchase: PurchaseHandler
}

/**
 * 客户端席位购买卡片。
 */
function ClientSeatCard({ pkg, onPurchase }: ClientSeatCardProps): ReactElement {
  const [seatInputValue, setSeatInputValue] = useState<string>(String(CLIENT_SEAT_MIN))
  const [monthInputValue, setMonthInputValue] = useState<string>(String(CLIENT_SEAT_MONTHS_MIN))
  const seatInputId = `client-seat-input-${pkg.id}`
  const monthInputId = `client-seat-month-input-${pkg.id}`

  const parsedQuantity = Number(seatInputValue)
  const parsedPurchaseMonths = Number(monthInputValue)
  const hasSeatInput = seatInputValue.trim().length > 0
  const hasMonthInput = monthInputValue.trim().length > 0
  const isIntegerQuantity = Number.isInteger(parsedQuantity)
  const isIntegerMonths = Number.isInteger(parsedPurchaseMonths)

  let seatValidationMessage: string | null = null
  if (!hasSeatInput) {
    seatValidationMessage = '请输入购买席位'
  } else if (!Number.isFinite(parsedQuantity) || !isIntegerQuantity) {
    seatValidationMessage = '请输入 1-20 的整数'
  } else if (parsedQuantity < CLIENT_SEAT_MIN) {
    seatValidationMessage = `最小值为 ${String(CLIENT_SEAT_MIN)}`
  } else if (parsedQuantity > CLIENT_SEAT_MAX) {
    seatValidationMessage = `最大值为 ${String(CLIENT_SEAT_MAX)}`
  }

  let monthsValidationMessage: string | null = null
  if (!hasMonthInput) {
    monthsValidationMessage = '请输入购买月数'
  } else if (!Number.isFinite(parsedPurchaseMonths) || !isIntegerMonths) {
    monthsValidationMessage = '请输入 1-12 的整数'
  } else if (parsedPurchaseMonths < CLIENT_SEAT_MONTHS_MIN) {
    monthsValidationMessage = `最小值为 ${String(CLIENT_SEAT_MONTHS_MIN)}`
  } else if (parsedPurchaseMonths > CLIENT_SEAT_MONTHS_MAX) {
    monthsValidationMessage = `最大值为 ${String(CLIENT_SEAT_MONTHS_MAX)}`
  }

  const validationMessage = seatValidationMessage ?? monthsValidationMessage
  const isValid = validationMessage === null
  const totalAmountYuan = isValid ? pkg.priceYuan * parsedQuantity * parsedPurchaseMonths : 0

  const handlePurchase = useCallback((): void => {
    if (!isValid) {
      return
    }
    onPurchase(pkg, { quantity: parsedQuantity, purchaseMonths: parsedPurchaseMonths })
  }, [isValid, onPurchase, parsedPurchaseMonths, parsedQuantity, pkg])

  const handleSeatStepChange = useCallback((step: number): void => {
    const current = Number(seatInputValue)
    const baseValue = Number.isFinite(current) ? Math.trunc(current) : CLIENT_SEAT_MIN
    const nextValue = Math.min(CLIENT_SEAT_MAX, Math.max(CLIENT_SEAT_MIN, baseValue + step))
    setSeatInputValue(String(nextValue))
  }, [seatInputValue])

  const handleMonthStepChange = useCallback((step: number): void => {
    const current = Number(monthInputValue)
    const baseValue = Number.isFinite(current) ? Math.trunc(current) : CLIENT_SEAT_MONTHS_MIN
    const nextValue = Math.min(
      CLIENT_SEAT_MONTHS_MAX,
      Math.max(CLIENT_SEAT_MONTHS_MIN, baseValue + step)
    )
    setMonthInputValue(String(nextValue))
  }, [monthInputValue])

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="group relative flex h-full flex-col rounded-2xl border border-sky-500/40 bg-card shadow-md shadow-sky-500/5 transition-all hover:-translate-y-1 hover:shadow-lg"
    >
      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
        <span className="inline-flex items-center gap-1 rounded-full bg-sky-600 px-3 py-1 text-xs font-semibold text-white shadow-sm">
          <Monitor className="h-3 w-3" />
          助手席位
        </span>
      </div>

      <div className="p-6 pb-4">
        <h3 className="text-base font-bold text-foreground">{pkg.name}</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          购买后可为成员分配商品采集助手授权
        </p>
      </div>

      <div className="flex-1 border-t border-dashed px-6 py-4">
        <div className="space-y-3">
          <div>
            <label htmlFor={seatInputId} className="text-sm font-medium text-foreground">
              席位数量
            </label>
            <div className="mt-1.5 flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-10 w-10"
                onClick={(): void => {
                  handleSeatStepChange(-1)
                }}
              >
                -
              </Button>
              <Input
                id={seatInputId}
                type="number"
                min={CLIENT_SEAT_MIN}
                max={CLIENT_SEAT_MAX}
                step={1}
                value={seatInputValue}
                onChange={(event): void => {
                  setSeatInputValue(event.target.value)
                }}
                className="h-10 text-center text-base"
                placeholder="请输入席位数量"
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-10 w-10"
                onClick={(): void => {
                  handleSeatStepChange(1)
                }}
              >
                +
              </Button>
            </div>
          </div>

          <div>
            <label htmlFor={monthInputId} className="text-sm font-medium text-foreground">
              购买月数
            </label>
            <div className="mt-1.5 flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-10 w-10"
                onClick={(): void => {
                  handleMonthStepChange(-1)
                }}
              >
                -
              </Button>
              <Input
                id={monthInputId}
                type="number"
                min={CLIENT_SEAT_MONTHS_MIN}
                max={CLIENT_SEAT_MONTHS_MAX}
                step={1}
                value={monthInputValue}
                onChange={(event): void => {
                  setMonthInputValue(event.target.value)
                }}
                className="h-10 text-center text-base"
                placeholder="请输入购买月数"
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-10 w-10"
                onClick={(): void => {
                  handleMonthStepChange(1)
                }}
              >
                +
              </Button>
            </div>
          </div>

          {validationMessage !== null ? (
            <p className="text-xs text-destructive">{validationMessage}</p>
          ) : (
            <p className="text-xs text-muted-foreground">
              席位 {CLIENT_SEAT_MIN}-{CLIENT_SEAT_MAX} 个，月数 {CLIENT_SEAT_MONTHS_MIN}-{CLIENT_SEAT_MONTHS_MAX} 个月（1个月=30天）
            </p>
          )}

          <div className="rounded-xl bg-muted/50 p-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">单价</span>
              <span className="text-sm font-medium text-foreground">¥{formatMoney(pkg.priceYuan)}/席位/月</span>
            </div>
            <div className="mt-2 flex items-center justify-between">
              <span className="text-sm text-muted-foreground">购买时长</span>
              <span className="text-sm font-medium text-foreground">
                {isValid
                  ? `${String(parsedPurchaseMonths)} 个月（${String(parsedPurchaseMonths * CLIENT_SEAT_DAYS_PER_MONTH)} 天）`
                  : '-'}
              </span>
            </div>
            <div className="mt-2 flex items-center justify-between">
              <span className="text-sm text-muted-foreground">应付总额</span>
              <div className="flex items-baseline gap-0.5">
                <span className="text-sm text-muted-foreground">¥</span>
                <span className="text-2xl font-black tracking-tight text-foreground">
                  {totalAmountYuan.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 pt-2">
        <Button className="w-full gap-1.5" size="lg" disabled={!isValid} onClick={handlePurchase}>
          立即购买
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </motion.div>
  )
}

interface ClientPlusCardProps {
  pkg: Package
  onPurchase: PurchaseHandler
}

function ClientPlusCard({ pkg, onPurchase }: ClientPlusCardProps): ReactElement {
  const [seatInputValue, setSeatInputValue] = useState<string>(String(CLIENT_SEAT_MIN))
  const [monthInputValue, setMonthInputValue] = useState<string>(String(CLIENT_SEAT_MONTHS_MIN))
  const seatInputId = `client-plus-input-${pkg.id}`
  const monthInputId = `client-plus-month-input-${pkg.id}`

  const parsedQuantity = Number(seatInputValue)
  const parsedPurchaseMonths = Number(monthInputValue)
  const hasSeatInput = seatInputValue.trim().length > 0
  const hasMonthInput = monthInputValue.trim().length > 0
  const isIntegerQuantity = Number.isInteger(parsedQuantity)
  const isIntegerMonths = Number.isInteger(parsedPurchaseMonths)

  let seatValidationMessage: string | null = null
  if (!hasSeatInput) {
    seatValidationMessage = '请输入购买席位'
  } else if (!Number.isFinite(parsedQuantity) || !isIntegerQuantity) {
    seatValidationMessage = '请输入 1-20 的整数'
  } else if (parsedQuantity < CLIENT_SEAT_MIN) {
    seatValidationMessage = `最小值为 ${String(CLIENT_SEAT_MIN)}`
  } else if (parsedQuantity > CLIENT_SEAT_MAX) {
    seatValidationMessage = `最大值为 ${String(CLIENT_SEAT_MAX)}`
  }

  let monthsValidationMessage: string | null = null
  if (!hasMonthInput) {
    monthsValidationMessage = '请输入购买月数'
  } else if (!Number.isFinite(parsedPurchaseMonths) || !isIntegerMonths) {
    monthsValidationMessage = '请输入 1-12 的整数'
  } else if (parsedPurchaseMonths < CLIENT_SEAT_MONTHS_MIN) {
    monthsValidationMessage = `最小值为 ${String(CLIENT_SEAT_MONTHS_MIN)}`
  } else if (parsedPurchaseMonths > CLIENT_SEAT_MONTHS_MAX) {
    monthsValidationMessage = `最大值为 ${String(CLIENT_SEAT_MONTHS_MAX)}`
  }

  const validationMessage = seatValidationMessage ?? monthsValidationMessage
  const isValid = validationMessage === null
  const totalAmountYuan = isValid ? pkg.priceYuan * parsedQuantity * parsedPurchaseMonths : 0

  const handlePurchase = useCallback((): void => {
    if (!isValid) {
      return
    }
    onPurchase(pkg, { quantity: parsedQuantity, purchaseMonths: parsedPurchaseMonths })
  }, [isValid, onPurchase, parsedPurchaseMonths, parsedQuantity, pkg])

  const handleSeatStepChange = useCallback((step: number): void => {
    const current = Number(seatInputValue)
    const baseValue = Number.isFinite(current) ? Math.trunc(current) : CLIENT_SEAT_MIN
    const nextValue = Math.min(CLIENT_SEAT_MAX, Math.max(CLIENT_SEAT_MIN, baseValue + step))
    setSeatInputValue(String(nextValue))
  }, [seatInputValue])

  const handleMonthStepChange = useCallback((step: number): void => {
    const current = Number(monthInputValue)
    const baseValue = Number.isFinite(current) ? Math.trunc(current) : CLIENT_SEAT_MONTHS_MIN
    const nextValue = Math.min(
      CLIENT_SEAT_MONTHS_MAX,
      Math.max(CLIENT_SEAT_MONTHS_MIN, baseValue + step)
    )
    setMonthInputValue(String(nextValue))
  }, [monthInputValue])

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="group relative flex h-full flex-col rounded-2xl border border-amber-500/40 bg-card shadow-md shadow-amber-500/5 transition-all hover:-translate-y-1 hover:shadow-lg"
    >
      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
        <span className="inline-flex items-center gap-1 rounded-full bg-amber-500 px-3 py-1 text-xs font-semibold text-amber-950 shadow-sm">
          <Crown className="h-3 w-3" />
          Plus 席位
        </span>
      </div>

      <div className="p-6 pb-4">
        <h3 className="text-base font-bold text-foreground">{pkg.name}</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          购买后可为成员开启“过滤已采集商品”能力
        </p>
      </div>

      <div className="flex-1 border-t border-dashed px-6 py-4">
        <div className="space-y-3">
          <div>
            <label htmlFor={seatInputId} className="text-sm font-medium text-foreground">
              席位数量
            </label>
            <div className="mt-1.5 flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-10 w-10"
                onClick={(): void => {
                  handleSeatStepChange(-1)
                }}
              >
                -
              </Button>
              <Input
                id={seatInputId}
                type="number"
                min={CLIENT_SEAT_MIN}
                max={CLIENT_SEAT_MAX}
                step={1}
                value={seatInputValue}
                onChange={(event): void => {
                  setSeatInputValue(event.target.value)
                }}
                className="h-10 text-center text-base"
                placeholder="请输入席位数量"
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-10 w-10"
                onClick={(): void => {
                  handleSeatStepChange(1)
                }}
              >
                +
              </Button>
            </div>
          </div>

          <div>
            <label htmlFor={monthInputId} className="text-sm font-medium text-foreground">
              购买月数
            </label>
            <div className="mt-1.5 flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-10 w-10"
                onClick={(): void => {
                  handleMonthStepChange(-1)
                }}
              >
                -
              </Button>
              <Input
                id={monthInputId}
                type="number"
                min={CLIENT_SEAT_MONTHS_MIN}
                max={CLIENT_SEAT_MONTHS_MAX}
                step={1}
                value={monthInputValue}
                onChange={(event): void => {
                  setMonthInputValue(event.target.value)
                }}
                className="h-10 text-center text-base"
                placeholder="请输入购买月数"
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-10 w-10"
                onClick={(): void => {
                  handleMonthStepChange(1)
                }}
              >
                +
              </Button>
            </div>
          </div>

          {validationMessage !== null ? (
            <p className="text-xs text-destructive">{validationMessage}</p>
          ) : (
            <p className="text-xs text-muted-foreground">
              席位 {CLIENT_SEAT_MIN}-{CLIENT_SEAT_MAX} 个，月数 {CLIENT_SEAT_MONTHS_MIN}-{CLIENT_SEAT_MONTHS_MAX} 个月（1个月=30天）
            </p>
          )}

          <div className="rounded-xl bg-amber-500/5 p-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">单价</span>
              <span className="text-sm font-medium text-foreground">¥{formatMoney(pkg.priceYuan)}/席位/月</span>
            </div>
            <div className="mt-2 flex items-center justify-between">
              <span className="text-sm text-muted-foreground">核心权益</span>
              <span className="text-sm font-medium text-foreground">过滤已采集商品</span>
            </div>
            <div className="mt-2 flex items-center justify-between">
              <span className="text-sm text-muted-foreground">购买时长</span>
              <span className="text-sm font-medium text-foreground">
                {isValid
                  ? `${String(parsedPurchaseMonths)} 个月（${String(parsedPurchaseMonths * CLIENT_SEAT_DAYS_PER_MONTH)} 天）`
                  : '-'}
              </span>
            </div>
            <div className="mt-2 flex items-center justify-between">
              <span className="text-sm text-muted-foreground">应付总额</span>
              <div className="flex items-baseline gap-0.5">
                <span className="text-sm text-muted-foreground">¥</span>
                <span className="text-2xl font-black tracking-tight text-foreground">
                  {totalAmountYuan.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 pt-2">
        <Button className="w-full gap-1.5" size="lg" disabled={!isValid} onClick={handlePurchase}>
          立即购买
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </motion.div>
  )
}

/**
 * 商品采集助手展示区。
 * @returns 展示区 ReactElement
 */
function ClientAssistantShowcase(): ReactElement {
  const [latestRelease, setLatestRelease] = useState<ExtensionLatestRelease | null>(null)
  const [isLoadingRelease, setIsLoadingRelease] = useState(true)
  const [releaseError, setReleaseError] = useState<string | null>(null)

  const loadLatestRelease = useCallback(async (): Promise<void> => {
    setIsLoadingRelease(true)
    setReleaseError(null)

    try {
      const release = await getLatestExtensionRelease(defaultReleaseChannel)
      startTransition(() => {
        setLatestRelease(release)
        setReleaseError(null)
      })
    } catch (error) {
      startTransition(() => {
        setLatestRelease(null)
        setReleaseError(error instanceof Error ? error.message : '暂时无法获取最新下载链接')
      })
    } finally {
      setIsLoadingRelease(false)
    }
  }, [])

  useEffect(() => {
    void loadLatestRelease()
  }, [loadLatestRelease])

  const handleDownloadClick = useCallback((): void => {
    if (latestRelease === null || latestRelease.downloadUrl === '') {
      setReleaseError('当前下载链接不可用，请稍后重试')
      return
    }

    const link = document.createElement('a')
    link.href = latestRelease.downloadUrl
    link.download = getReleaseDownloadFilename(latestRelease)
    link.style.display = 'none'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }, [latestRelease])

  const releasePublishedAtLabel =
    latestRelease === null ? null : formatReleasePublishedAt(latestRelease.publishedAt)
  const hasValidReleaseDownload = latestRelease !== null && latestRelease.downloadUrl !== ''

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="relative overflow-hidden rounded-[28px] border border-sky-500/20 bg-gradient-to-br from-sky-500/[0.14] via-background to-cyan-500/[0.10] p-6 shadow-[0_24px_80px_-44px_rgba(14,165,233,0.55)] sm:p-8"
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(56,189,248,0.18),transparent_38%),radial-gradient(circle_at_bottom_right,rgba(34,211,238,0.14),transparent_36%)]" />
      <div className="absolute -right-10 top-8 h-32 w-32 rounded-full bg-sky-500/10 blur-3xl" />

      <div className="relative space-y-6">
        <div className="inline-flex items-center gap-2 rounded-full border border-sky-500/20 bg-background/75 px-3 py-1 text-xs font-medium text-sky-700 shadow-sm backdrop-blur dark:text-sky-200">
          <Bot className="h-3.5 w-3.5" />
          商品采集助手
        </div>

        <div className="grid gap-6 xl:grid-cols-[minmax(0,1.2fr)_minmax(280px,0.8fr)] xl:items-start">
          <div>
            <h2 className="text-2xl font-black tracking-tight text-foreground sm:text-3xl">
              批量采集商品信息，高效快捷
            </h2>
            <p className="mt-3 text-sm leading-6 text-muted-foreground sm:text-base">
              可辅助批量采集网站商品信息，并同步完成图片自动裁剪与素材规整，适合持续处理商品素材的团队。
            </p>

            <div className="mt-4 flex flex-wrap gap-2">
              {['批量采集', '自动裁剪', '素材规整'].map((tag) => (
                <span
                  key={tag}
                  className="rounded-full border border-border/60 bg-background/75 px-3 py-1 text-xs text-foreground/80 backdrop-blur"
                >
                  {tag}
                </span>
              ))}
            </div>

            <div className="mt-5 rounded-2xl border border-dashed border-sky-500/30 bg-background/55 p-4 backdrop-blur">
              <p className="text-sm font-semibold text-foreground">适合场景</p>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">
                适合电商运营、采销、设计等角色协作处理商品标题、价格、规格与图片素材。
              </p>
            </div>
          </div>

          <div className="space-y-3">
            {CLIENT_ASSISTANT_FEATURES.map((feature) => (
              <div
                key={feature.title}
                className="rounded-2xl border border-border/50 bg-background/75 p-4 shadow-sm backdrop-blur"
              >
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-sky-500/10 text-sky-600 dark:text-sky-300">
                    <feature.icon className="h-4.5 w-4.5" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">{feature.title}</h3>
                    <p className="mt-1 text-xs leading-5 text-muted-foreground">{feature.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-border/50 bg-background/60 p-4 shadow-sm backdrop-blur">
          <p className="text-sm font-semibold text-foreground">工作流</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {CLIENT_ASSISTANT_STEPS.map((item) => (
              <div
                key={item.step}
                className="inline-flex items-center gap-2 rounded-full bg-muted/70 px-3 py-1.5 text-xs text-muted-foreground"
              >
                <span className="font-semibold text-sky-600 dark:text-sky-300">{item.step}</span>
                <span>{item.title}</span>
              </div>
            ))}
          </div>
        </div>

        <div
          id={CLIENT_ASSISTANT_DOWNLOAD_ID}
          className="relative scroll-mt-40 overflow-hidden rounded-[24px] border border-sky-500/25 bg-gradient-to-r from-sky-500/[0.16] via-background/92 to-cyan-500/[0.14] p-5 shadow-[0_18px_56px_-30px_rgba(14,165,233,0.45)]"
        >
          <div className="absolute -bottom-10 right-0 h-28 w-28 rounded-full bg-cyan-400/15 blur-3xl" />

          <div className="relative flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-sky-500/20 bg-background/80 px-3 py-1 text-xs font-medium text-sky-700 shadow-sm backdrop-blur dark:text-sky-200">
                <Download className="h-3.5 w-3.5" />
                客户端下载
              </span>
              <h3 className="mt-3 text-lg font-black tracking-tight text-foreground sm:text-xl">
                下载商品采集助手
              </h3>
              <p className="mt-1.5 text-sm leading-6 text-muted-foreground">
                下载后完成安装，即可开始批量采集和整理商品素材。
              </p>
              {latestRelease !== null ? (
                <p className="mt-2 text-xs text-muted-foreground">
                  最新版本 {latestRelease.version}
                  {releasePublishedAtLabel !== null ? ` · 发布于 ${releasePublishedAtLabel}` : ''}
                </p>
              ) : null}
              {releaseError !== null ? (
                <p className="mt-2 text-xs text-destructive">
                  {releaseError}
                </p>
              ) : null}
            </div>

            <div className="flex flex-col items-stretch gap-2 sm:items-end">
              <Button
                size="lg"
                className="rounded-xl bg-sky-600 text-white shadow-sm shadow-sky-500/25 hover:bg-sky-500"
                disabled={isLoadingRelease || !hasValidReleaseDownload}
                onClick={handleDownloadClick}
              >
                {isLoadingRelease ? '加载中...' : '立即下载'}
                <Download className="h-4 w-4" />
              </Button>
              {latestRelease === null && releaseError === null && !isLoadingRelease ? (
                <p className="text-xs text-muted-foreground">当前暂无可下载版本</p>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  )
}

/**
 * 加载状态骨架屏。
 */
function LoadingSkeleton(): ReactElement {
  return (
    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
      {[1, 2, 3, 4].map((item) => (
        <div key={item} className="animate-pulse rounded-2xl border bg-card p-6">
          <div className="h-5 w-24 rounded bg-muted" />
          <div className="mt-5 h-10 w-28 rounded bg-muted" />
          <div className="mt-5 space-y-2.5 border-t border-dashed pt-4">
            <div className="h-4 w-full rounded bg-muted" />
            <div className="h-4 w-4/5 rounded bg-muted" />
            <div className="h-4 w-3/5 rounded bg-muted" />
          </div>
          <div className="mt-5 h-10 w-full rounded bg-muted" />
        </div>
      ))}
    </div>
  )
}

/**
 * 错误状态组件 Props。
 */
interface ErrorStateProps {
  message: string
  onRetry: () => void
}

/**
 * 错误状态组件。
 */
function ErrorState({ message, onRetry }: ErrorStateProps): ReactElement {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border bg-card px-6 py-16 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
        <AlertCircle className="h-6 w-6 text-destructive" />
      </div>
      <h2 className="mt-4 text-lg font-bold">加载失败</h2>
      <p className="mt-1.5 max-w-md text-sm text-muted-foreground">{message}</p>
      <Button className="mt-5" variant="outline" size="sm" onClick={onRetry}>
        重新加载
      </Button>
    </div>
  )
}

/**
 * 空状态组件。
 */
function EmptyState(): ReactElement {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border bg-card px-6 py-16 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
        <Sparkles className="h-6 w-6 text-muted-foreground" />
      </div>
      <h2 className="mt-4 text-lg font-bold">暂无套餐</h2>
      <p className="mt-1.5 text-sm text-muted-foreground">当前暂无可购买方案，请稍后再试。</p>
    </div>
  )
}

/**
 * 会员内容区域 Props。
 */
interface SubscriptionContentProps {
  packages: GroupedPackages
  onPurchase: PurchaseHandler
}

/**
 * 会员内容区域。
 */
function SubscriptionContent({ packages, onPurchase }: SubscriptionContentProps): ReactElement {
  const allPackages = packages.subscription
  if (allPackages.length === 0) {
    return (
      <div className="py-12 text-center">
        <p className="text-muted-foreground">暂无会员套餐</p>
      </div>
    )
  }

  return (
    <div className="mx-auto grid max-w-6xl gap-5 sm:grid-cols-2 lg:grid-cols-4">
      {allPackages.map((pkg) => (
        <SubscriptionCard key={pkg.id} pkg={pkg} onPurchase={onPurchase} />
      ))}
    </div>
  )
}

/**
 * 算力包内容区域 Props。
 */
interface RechargeContentProps {
  packages: Package[]
  onPurchase: PurchaseHandler
}

/**
 * 算力包内容区域。
 */
function RechargeContent({ packages, onPurchase }: RechargeContentProps): ReactElement {
  if (packages.length === 0) {
    return (
      <div className="py-12 text-center">
        <p className="text-muted-foreground">暂无算力包</p>
      </div>
    )
  }

  return (
    <div className="mx-auto grid max-w-6xl gap-5 sm:grid-cols-2 lg:grid-cols-4">
      {packages.map((pkg) => (
        pkg.allowCustomPurchase && pkg.customPricePer100 > 0 ? (
          <CustomRechargePanel key={pkg.id} pkg={pkg} onPurchase={onPurchase} />
        ) : (
          <RechargeCard key={pkg.id} pkg={pkg} onPurchase={onPurchase} />
        )
      ))}
    </div>
  )
}

/**
 * 采集客户端内容区域 Props。
 */
interface ClientSeatContentProps {
  packages: Package[]
  plusPackages: Package[]
  onPurchase: PurchaseHandler
}

/**
 * 采集客户端内容区域。
 */
function ClientSeatContent({ packages, plusPackages, onPurchase }: ClientSeatContentProps): ReactElement {
  if (packages.length === 0 && plusPackages.length === 0) {
    return (
      <div className="py-12 text-center">
        <p className="text-muted-foreground">暂无商品采集助手套餐</p>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-xl font-black tracking-tight text-foreground">选择商品采集助手席位</h2>
          <p className="mt-1.5 text-sm text-muted-foreground">
            按成员数量和使用周期灵活配置，购买后即可分配团队授权。
          </p>
        </div>
        <span className="inline-flex items-center gap-1 rounded-full bg-sky-500/10 px-3 py-1 text-xs font-medium text-sky-700 dark:text-sky-200">
          <ShieldCheck className="h-3.5 w-3.5" />
          支持按需扩容席位
        </span>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(320px,1fr)_minmax(0,2fr)] lg:items-start">
        <div className="space-y-5">
          {packages.map((pkg) => (
            <ClientSeatCard key={pkg.id} pkg={pkg} onPurchase={onPurchase} />
          ))}
          {plusPackages.map((pkg) => (
            <ClientPlusCard key={pkg.id} pkg={pkg} onPurchase={onPurchase} />
          ))}
        </div>

        <div>
          <ClientAssistantShowcase />
        </div>
      </div>
    </div>
  )
}

/**
 * 底部信任指示条。
 */
function TrustBar(): ReactElement {
  const items = [
    { icon: LockKeyhole, text: '安全支付' },
    { icon: Zap, text: '即时到账' },
    { icon: ShieldCheck, text: '无隐藏费用' },
  ] as const

  return (
    <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-2">
      {items.map((item) => (
        <div key={item.text} className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <item.icon className="h-3.5 w-3.5" />
          <span>{item.text}</span>
        </div>
      ))}
    </div>
  )
}

/**
 * 套餐购买页面组件。
 * @returns 页面 ReactElement
 */
export function PricingPage(): ReactElement {
  const location = useLocation()
  const navigate = useNavigate()
  const token = useAuthStore((state) => state.token)

  const [activeTab, setActiveTab] = useState<MainTabType>('subscription')
  const [paymentDialogOpen, setPaymentDialogOpen] = useState<boolean>(false)
  const [selectedPackage, setSelectedPackage] = useState<Package | null>(null)
  const [selectedCustomCredits, setSelectedCustomCredits] = useState<number | null>(null)
  const [selectedSeatQuantity, setSelectedSeatQuantity] = useState<number | null>(null)
  const [selectedPurchaseMonths, setSelectedPurchaseMonths] = useState<number | null>(null)

  const {
    data: packages,
    isLoading,
    isError,
    error,
    refetch,
  } = useAllPackages()

  const handlePurchase = useCallback((pkg: Package, options?: PurchaseOptions): void => {
    if (token === null) {
      void navigate('/login', { state: { from: '/pricing' } })
      return
    }

    setSelectedPackage(pkg)
    setSelectedCustomCredits(options?.customCredits ?? null)
    setSelectedSeatQuantity(options?.quantity ?? null)
    setSelectedPurchaseMonths(options?.purchaseMonths ?? null)
    setPaymentDialogOpen(true)
  }, [navigate, token])

  const handlePaymentSuccess = useCallback((): void => {
    void refetch()
  }, [refetch])

  const handlePaymentDialogClose = useCallback((open: boolean): void => {
    setPaymentDialogOpen(open)
    if (!open) {
      setSelectedPackage(null)
      setSelectedCustomCredits(null)
      setSelectedSeatQuantity(null)
      setSelectedPurchaseMonths(null)
    }
  }, [])

  const handleTabChange = useCallback((tab: MainTabType): void => {
    setActiveTab(tab)

    if (!isClientAssistantDownloadHash(location.hash) || tab === 'clientSeat') {
      return
    }

    void navigate({ pathname: '/pricing', search: location.search }, { replace: true })
  }, [location.hash, location.search, navigate])

  const hasAnyPackages =
    packages !== undefined &&
    (
      packages.subscription.length > 0 ||
      packages.recharge.length > 0 ||
      packages.clientSeat.length > 0 ||
      packages.clientPlus.length > 0
    )
  const hasSubscription = packages !== undefined && packages.subscription.length > 0
  const hasRecharge = packages !== undefined && packages.recharge.length > 0
  const hasClientSeat =
    packages !== undefined &&
    (packages.clientSeat.length > 0 || packages.clientPlus.length > 0)
  const availableTabs: MainTabType[] = [
    ...(hasSubscription ? ['subscription'] as const : []),
    ...(hasRecharge ? ['recharge'] as const : []),
    ...(hasClientSeat ? ['clientSeat'] as const : []),
  ]
  const shouldFocusClientAssistantDownload =
    hasClientSeat && isClientAssistantDownloadHash(location.hash)

  const effectiveTab: MainTabType =
    shouldFocusClientAssistantDownload
      ? 'clientSeat'
      : availableTabs.includes(activeTab)
      ? activeTab
      : availableTabs[0] ?? 'subscription'

  useEffect(() => {
    if (!shouldFocusClientAssistantDownload || effectiveTab !== 'clientSeat') {
      return
    }

    const frameID = window.requestAnimationFrame(() => {
      const targetElement = document.getElementById(CLIENT_ASSISTANT_DOWNLOAD_ID)
      if (targetElement === null) {
        return
      }
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })

    return (): void => {
      window.cancelAnimationFrame(frameID)
    }
  }, [effectiveTab, shouldFocusClientAssistantDownload])

  return (
    <div className="mx-auto max-w-6xl space-y-8 px-4 py-8 sm:px-6">
      {/* 页头：标题 + Tab */}
      <div className="flex flex-col items-center gap-4 text-center">
        <div>
          <h1 className="text-2xl font-black tracking-tight text-foreground sm:text-3xl">
            选择适合你的方案
          </h1>
          <p className="mt-1.5 text-sm text-muted-foreground">
            {effectiveTab === 'subscription'
              ? '会员套餐适合长期稳定使用，含更多权益与赠送算力'
              : effectiveTab === 'recharge'
                ? '算力包按需购买，永久有效，适合灵活补充'
                : '商品采集助手可辅助批量采集网站商品信息，并支持自动裁剪与素材整理'}
          </p>
        </div>

        {availableTabs.length > 1 ? (
          <MainTabSwitcher
            activeTab={effectiveTab}
            hasSubscription={hasSubscription}
            hasRecharge={hasRecharge}
            hasClientSeat={hasClientSeat}
            onTabChange={handleTabChange}
          />
        ) : null}
      </div>

      {/* 内容区 */}
      {isLoading ? (
        <LoadingSkeleton />
      ) : isError ? (
        <ErrorState
          message={error instanceof Error ? error.message : '加载套餐信息失败，请重试'}
          onRetry={(): void => {
            void refetch()
          }}
        />
      ) : !hasAnyPackages ? (
        <EmptyState />
      ) : effectiveTab === 'subscription' ? (
        <SubscriptionContent packages={packages} onPurchase={handlePurchase} />
      ) : effectiveTab === 'recharge' ? (
        <RechargeContent packages={packages.recharge} onPurchase={handlePurchase} />
      ) : (
        <ClientSeatContent
          packages={packages.clientSeat}
          plusPackages={packages.clientPlus}
          onPurchase={handlePurchase}
        />
      )}

      {/* 信任条 */}
      {hasAnyPackages ? <TrustBar /> : null}

      <PaymentDialog
        open={paymentDialogOpen}
        onOpenChange={handlePaymentDialogClose}
        selectedPackage={selectedPackage}
        customCredits={selectedCustomCredits}
        seatQuantity={selectedSeatQuantity}
        purchaseMonths={selectedPurchaseMonths}
        onPaymentSuccess={handlePaymentSuccess}
      />
    </div>
  )
}
